// PIM Types for TypeScript

// Base interfaces
export interface PimAttributeGroup {
  id: number;
  name: string;
  code: string;
  description?: string;
  sort_order: number;
  is_global: boolean;
  product_type_id?: number;
  created_at: string;
  updated_at: string;
  attributes?: PimAttribute[];
  productType?: ProductType;
}

export interface PimAttribute {
  id: number;
  group_id: number;
  label: string; // Changed from 'name' to match backend DB schema
  code: string;
  type: PimAttributeType;
  description?: string;
  position?: number; // Optional - backend uses sort_order
  sort_order?: number; // Backend field name
  required?: boolean; // Backend field name
  is_required?: boolean; // Alternative name
  unique?: boolean; // Backend field name
  is_unique?: boolean; // Alternative name
  is_filterable?: boolean;
  is_searchable?: boolean;
  validation_json?: PimAttributeValidation;
  options_json?: PimAttributeOptions;
  is_public: boolean; // Whether attribute is visible via public API
  is_active: boolean; // Whether attribute is active/enabled in system
  scope?: 'global' | 'productType'; // Backend field
  product_type_id?: number; // Backend field for productType scope
  created_at: string;
  updated_at: string;
  group?: PimAttributeGroup;
}

export interface ProductType {
  id: number;
  name: string;
  code: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  products?: Product[];
}

export interface Product {
  id: number;
  sku: string;
  name: string;
  slug: string;
  status: ProductStatus;
  description?: string;
  product_type_id?: number;
  created_at: string;
  updated_at: string;
  productType?: ProductType;
  attributeValues?: PimProductAttributeValue[];
  categories?: Category[];
  variants?: ProductRelationship[];
  variantOptions?: ProductVariantOption[];
  relationships?: ProductRelationship[];
  // Helper property for form handling
  dynamicAttributes?: Record<string, any>;
}

export interface PimProductAttributeValue {
  id: number;
  product_id: number;
  attribute_id: number;
  value_string?: string;
  value_text?: string;
  value_number?: number;
  value_boolean?: boolean;
  value_date?: string;
  value_json?: any;
  created_at: string;
  updated_at: string;
  attribute?: PimAttribute;
  product?: Product;
}

export interface ApiKey {
  id: number;
  name: string;
  description?: string;
  key?: string; // Full key only shown during creation
  maskedKey?: string; // Masked version for display
  scopes: string[];
  is_active: boolean;
  rate_limit_per_min?: number;
  last_used_at?: string;
  last_used_ip?: string;
  usage_count: number;
  expires_at?: string;
  owner_user_id?: number;
  created_at: string;
  updated_at: string;
  owner?: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
}

// Enums and constants
export const PimAttributeType = {
  STRING: 'string',
  TEXT: 'text',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  DATE: 'date',
  ENUM: 'enum',
  MULTISELECT: 'multiselect',
  JSON: 'json',
  RELATION: 'relation',
  MEDIA: 'media'
} as const;

export type PimAttributeType = typeof PimAttributeType[keyof typeof PimAttributeType];

export const ProductStatus = {
  DRAFT: 'draft',
  PUBLISHED: 'published'
} as const;

export type ProductStatus = typeof ProductStatus[keyof typeof ProductStatus];

export const ApiKeyScope = {
  PIM_READ: 'pim.read',
  PIM_WRITE: 'pim.write',
  PIM_ADMIN: 'pim.admin',
  PRODUCTS_READ: 'products.read',
  PRODUCTS_WRITE: 'products.write',
  PRODUCTS_ADMIN: 'products.admin',
  ATTRIBUTES_READ: 'attributes.read',
  ATTRIBUTES_WRITE: 'attributes.write',
  ATTRIBUTES_ADMIN: 'attributes.admin'
} as const;

export type ApiKeyScope = typeof ApiKeyScope[keyof typeof ApiKeyScope];

// Validation and options interfaces
export interface PimAttributeValidation {
  // String validations
  maxLength?: number;
  minLength?: number;
  regex?: string;
  
  // Number validations
  min?: number;
  max?: number;
  decimalPlaces?: number;
  unit?: string;
  
  // Multiselect validations
  maxItems?: number;
  
  // General validations
  required?: boolean;
}

export interface PimAttributeOptions {
  // For enum and multiselect
  options?: string[];
  
  // For relation attributes
  entityType?: string;
  entityField?: string;
  
  // For media attributes
  allowedTypes?: string[];
  maxFileSize?: number;
  maxFiles?: number;
}

// Form interfaces
export interface ProductFormData {
  sku: string;
  name: string;
  slug?: string;
  status: ProductStatus;
  description?: string;
  product_type_id?: number;
  dynamicAttributes: Record<string, any>;
}

export interface PimAttributeFormData {
  group_id: number;
  label?: string; // Backend uses 'label'
  name?: string; // Alternative name
  code: string;
  type: PimAttributeType;
  description?: string;
  position?: number;
  sort_order?: number; // Backend uses sort_order
  is_required?: boolean;
  required?: boolean; // Backend uses 'required'
  is_unique?: boolean;
  unique?: boolean; // Backend uses 'unique'
  is_filterable?: boolean;
  is_searchable?: boolean;
  validation_json?: PimAttributeValidation;
  options_json?: PimAttributeOptions;
  is_public?: boolean; // Whether visible via public API
  is_active?: boolean; // Whether active/enabled in system
  scope?: 'global' | 'productType';
  product_type_id?: number;
}

export interface PimAttributeGroupFormData {
  name: string;
  code: string;
  description?: string;
  sortOrder: number;
  isGlobal: boolean;
  productTypeId?: number;
}

export interface ProductTypeFormData {
  name: string;
  code: string;
  description?: string;
  is_active: boolean;
  sort_order?: number;
}

export interface ApiKeyFormData {
  name: string;
  description?: string;
  scopes: string[];
  expiresAt?: string;
}

export interface ApiKeyUsageStatistics {
  keyId: number;
  totalRequests: number;
  requestsToday: number;
  requestsThisHour: number;
  averageRequestsPerDay: number;
  lastActiveDate?: string;
  mostActiveDate?: string;
  errorCount: number;
  errorRate: number;
}

export interface ApiKeyScopeDefinition {
  scope: string;
  name: string;
  description: string;
  category: 'read' | 'write' | 'admin';
  parent?: string;
  children?: string[];
}

// Filter interfaces
export interface ProductFilters {
  search?: string;
  status?: ProductStatus[];
  product_type_id?: number[];
  attribute_filters?: Record<string, any>;
  has_media?: boolean;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface PimAttributeFilters {
  search?: string;
  group_id?: number[];
  type?: PimAttributeType[];
  is_required?: boolean;
  is_active?: boolean;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface PimAttributeGroupFilters {
  search?: string;
  is_global?: boolean;
  product_type_id?: number;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface ProductTypeFilters {
  search?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface ApiKeyFilters {
  search?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

// API Response interfaces
export interface PimApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PimPaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Statistics interfaces
export interface PimStatistics {
  totalProducts: number;
  publishedProducts: number;
  draftProducts: number;
  totalAttributes: number;
  activeAttributes: number;
  totalProductTypes: number;
  activeProductTypes: number;
  totalAttributeGroups: number;
  activeAttributeGroups: number;
}

// Bulk operation interfaces
export interface ProductBulkOperation {
  action: 'publish' | 'unpublish' | 'delete';
  productIds: number[];
}

export interface BulkProductOperation {
  action: 'publish' | 'unpublish' | 'delete' | 'update_type';
  productIds: number[];
  data?: any;
}

export interface BulkOperationResult {
  success: boolean;
  processed: number;
  failed: number;
  errors?: Array<{
    productId: number;
    error: string;
  }>;
}

// Advanced attribute management interfaces
export interface AttributeUsageStatistics {
  attributeId: number;
  productCount: number;
  usedProductCount: number;
  emptyProductCount: number;
  fillRate: number;
  lastUsed?: string;
  mostRecentProduct?: string;
  averageValueLength?: number;
}

export interface AttributePreview {
  attribute: PimAttributeFormData;
  sampleValue: any;
}

export interface ValidationTest {
  input: string;
  isValid: boolean;
  errorMessage?: string;
}

export interface AttributeBulkOperation {
  action: 'activate' | 'deactivate' | 'delete' | 'change_scope' | 'change_group';
  attributeIds: number[];
  data?: {
    groupId?: number;
    scope?: 'global' | 'productType';
    productTypeId?: number;
  };
}

export interface AttributeBulkOperationResult {
  success: boolean;
  processed: number;
  failed: number;
  errors?: Array<{
    attributeId: number;
    error: string;
  }>;
}

// Extended attribute interfaces for scope management
export interface ScopedPimAttributeFormData extends PimAttributeFormData {
  scope?: 'global' | 'productType';
  product_type_id?: number;
  is_public?: boolean;
  default_value?: string;
}

// Import/Export interfaces
export interface AttributeImportData {
  attributes: Array<Omit<PimAttributeFormData, 'id'>>;
  groups?: Array<Omit<PimAttributeGroupFormData, 'id'>>;
}

export interface AttributeExportData {
  attributes: PimAttribute[];
  groups: PimAttributeGroup[];
  exportDate: string;
  version: string;
}

// Category interfaces
export interface Category {
  id: number;
  name: string;
  slug: string;
  description?: string;
  parent_id?: number;
  level: number;
  path?: string;
  sort_order: number;
  is_active: boolean;
  image_url?: string;
  meta_title?: string;
  meta_description?: string;
  created_at: string;
  updated_at: string;
  parent?: Category;
  children?: Category[];
  products?: Product[];
}

export interface CategoryFormData {
  name: string;
  slug?: string;
  description?: string;
  parentId?: number;
  sortOrder?: number;
  isActive?: boolean;
  imageUrl?: string;
  metaTitle?: string;
  metaDescription?: string;
}

export interface CategoryFilters {
  parentId?: number;
  includeProducts?: boolean;
  includeInactive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

// Product Relationship interfaces
export interface ProductRelationship {
  id: number;
  parent_product_id: number;
  child_product_id: number;
  relationship_type: ProductRelationshipType;
  sort_order: number;
  quantity?: number;
  metadata?: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  parentProduct?: Product;
  childProduct?: Product;
}

export interface ProductRelationshipFormData {
  childProductId: number;
  relationshipType: ProductRelationshipType;
  quantity?: number;
  sortOrder?: number;
  metadata?: any;
}

export const ProductRelationshipType = {
  VARIANT: 'variant',
  BUNDLE: 'bundle',
  RELATED: 'related',
  ACCESSORY: 'accessory',
  UPSELL: 'upsell',
  CROSS_SELL: 'cross_sell'
} as const;

export type ProductRelationshipType = typeof ProductRelationshipType[keyof typeof ProductRelationshipType];

// Product Variant Option interfaces
export interface ProductVariantOption {
  id: number;
  product_id: number;
  attribute_id: number;
  option_value: string;
  sku_suffix?: string;
  price_modifier?: number;
  stock_quantity?: number;
  is_default: boolean;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  product?: Product;
  attribute?: PimAttribute;
}

export interface ProductVariantOptionFormData {
  attributeId: number;
  optionValue: string;
  skuSuffix?: string;
  priceModifier?: number;
  stockQuantity?: number;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface VariantMatrixData {
  variants: ProductVariantOptionFormData[];
}

// Category assignment interface
export interface ProductCategoryAssignment {
  categoryIds: number[];
  primaryCategoryId?: number;
}

// Filter extensions
export interface ProductFiltersExtended extends ProductFilters {
  category_id?: number[];
  has_variants?: boolean;
  is_variant?: boolean;
}

// API response interfaces for new entities
export interface CategoryTreeResponse {
  success: boolean;
  data: Category[];
}

export interface ProductVariantResponse {
  success: boolean;
  data: ProductRelationship[];
}

export interface ProductVariantOptionsResponse {
  success: boolean;
  data: ProductVariantOption[];
}

export interface ProductRelationshipsResponse {
  success: boolean;
  data: ProductRelationship[];
}

// Bulk operations for categories and variants
export interface CategoryBulkOperation {
  action: 'activate' | 'deactivate' | 'delete' | 'move';
  categoryIds: number[];
  data?: {
    parentId?: number;
  };
}

export interface VariantBulkOperation {
  action: 'activate' | 'deactivate' | 'delete' | 'update_stock';
  variantIds: number[];
  data?: {
    stockQuantity?: number;
  };
}

// Media and Translation interfaces
export interface PimMedia {
  id: number;
  product_id?: number;
  library_id?: number;
  kind: 'image' | 'document';
  mime: string;
  original_name: string;
  stored_name: string;
  size: number;
  width?: number;
  height?: number;
  title?: string;
  alt_text?: string;
  description?: string;
  tags?: string[];
  metadata?: any;
  url?: string;
  storage_path?: string;
  thumbnail_url?: string;
  medium_url?: string;
  large_url?: string;
  webp_url?: string;
  upload_by?: number;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  library?: MediaLibrary;
  uploader?: any;
  product?: Product;
}

export interface MediaLibrary {
  id: number;
  name: string;
  slug: string;
  description?: string;
  type: 'product' | 'category' | 'general';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  media?: PimMedia[];
}

export interface ProductMedia {
  id: number;
  product_id: number;
  media_id: number;
  type: 'main' | 'gallery' | 'document' | 'video';
  position: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  media?: PimMedia;
  product?: Product;
}

export interface CategoryMedia {
  id: number;
  category_id: number;
  media_id: number;
  type: 'banner' | 'thumbnail' | 'icon';
  position: number;
  created_at: string;
  updated_at: string;
  media?: PimMedia;
  category?: Category;
}

export interface ProductTranslation {
  id: number;
  product_id: number;
  locale: string;
  name: string;
  slug?: string;
  description?: string;
  short_description?: string;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  created_at: string;
  updated_at: string;
  product?: Product;
}

export interface CategoryTranslation {
  id: number;
  category_id: number;
  locale: string;
  name: string;
  slug?: string;
  description?: string;
  meta_title?: string;
  meta_description?: string;
  created_at: string;
  updated_at: string;
  category?: Category;
}

export interface AttributeTranslation {
  id: number;
  attribute_id: number;
  locale: string;
  label: string;
  description?: string;
  created_at: string;
  updated_at: string;
  attribute?: PimAttribute;
}

// Form data interfaces
export interface MediaLibraryFormData {
  name: string;
  slug: string;
  description?: string;
  type: 'product' | 'category' | 'general';
  is_active?: boolean;
}

export interface PimMediaFormData {
  product_id?: number;
  library_id?: number;
  title?: string;
  alt_text?: string;
  description?: string;
  tags?: string[];
  is_public?: boolean;
}

export interface ProductTranslationFormData {
  name: string;
  slug?: string;
  description?: string;
  short_description?: string;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
}

export interface CategoryTranslationFormData {
  name: string;
  slug?: string;
  description?: string;
  meta_title?: string;
  meta_description?: string;
}

// Filter interfaces
export interface MediaFilters {
  library_id?: number;
  kind?: 'image' | 'document';
  product_id?: number;
  is_public?: boolean;
  page?: number;
  limit?: number;
}

export interface MediaLibraryFilters {
  type?: 'product' | 'category' | 'general';
  is_active?: boolean;
  page?: number;
  limit?: number;
}

// Response interfaces
export interface MediaLibraryResponse {
  success: boolean;
  data: MediaLibrary[];
  pagination?: any;
}

export interface PimMediaResponse {
  success: boolean;
  data: {
    media: PimMedia[];
    pagination: {
      total: number;
      pages: number;
      current: number;
      limit: number;
    };
  };
}

export interface ProductMediaResponse {
  success: boolean;
  data: ProductMedia[];
}

export interface TranslationImportResult {
  created: number;
  updated: number;
  errors: string[];
}

// Upload interfaces
export interface MediaUploadData {
  file: File;
  library_id?: number;
  product_id?: number;
  title?: string;
  alt_text?: string;
  description?: string;
  tags?: string[];
  is_public?: boolean;
}

export interface MediaUploadProgress {
  file: string;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
}

// Supported locales
export const SUPPORTED_LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'pl', name: 'Polski' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'it', name: 'Italiano' }
] as const;

export type SupportedLocale = typeof SUPPORTED_LOCALES[number]['code'];

// Workflow System Types
export interface ProductWorkflowState {
  id: number;
  product_id: number;
  current_state: WorkflowState;
  previous_state?: WorkflowState;
  assigned_reviewer_id?: number;
  submitted_by?: number;
  submitted_at?: string;
  reviewed_by?: number;
  reviewed_at?: string;
  published_by?: number;
  published_at?: string;
  rejection_reason?: string;
  notes?: string;
  due_date?: string;
  priority: WorkflowPriority;
  created_at: string;
  updated_at: string;
  assignedReviewer?: User;
  submitter?: User;
  reviewer?: User;
  publisher?: User;
  product?: Product;
}

export interface ProductWorkflowHistory {
  id: number;
  product_id: number;
  from_state?: WorkflowState;
  to_state: WorkflowState;
  action_by: number;
  action_type: WorkflowActionType;
  comment?: string;
  metadata?: any;
  created_at: string;
  user?: User;
}

export interface ProductComment {
  id: number;
  product_id: number;
  user_id: number;
  parent_id?: number;
  comment: string;
  comment_type: CommentType;
  is_internal: boolean;
  is_resolved: boolean;
  resolved_by?: number;
  resolved_at?: string;
  attachments?: any[];
  created_at: string;
  updated_at: string;
  author?: User;
  resolver?: User;
  parent?: ProductComment;
  replies?: ProductComment[];
}

export interface ProductRevision {
  id: number;
  product_id: number;
  revision_number: number;
  title?: string;
  description?: string;
  created_by: number;
  changes_summary?: string;
  product_data: any;
  attributes_data?: any;
  categories_data?: any;
  media_data?: any;
  translations_data?: any;
  is_current: boolean;
  tags?: string[];
  size_bytes?: number;
  created_at: string;
  creator?: User;
  product?: Product;
}

export interface ProductChangeLog {
  id: number;
  product_id: number;
  revision_id?: number;
  user_id: number;
  action: ChangeAction;
  entity_type: EntityType;
  entity_id?: number;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  change_context?: any;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  user?: User;
  revision?: ProductRevision;
  product?: Product;
}

// Workflow Enums
export type WorkflowState = 
  | 'draft' 
  | 'pending_review' 
  | 'in_review' 
  | 'approved' 
  | 'published' 
  | 'rejected' 
  | 'archived';

export type WorkflowPriority = 'low' | 'medium' | 'high' | 'urgent';

export type WorkflowActionType = 
  | 'submit' 
  | 'approve' 
  | 'reject' 
  | 'publish' 
  | 'archive' 
  | 'revert' 
  | 'assign';

export type CommentType = 
  | 'general' 
  | 'review' 
  | 'approval' 
  | 'rejection' 
  | 'internal';

export type ChangeAction = 
  | 'create' 
  | 'update' 
  | 'delete' 
  | 'publish' 
  | 'unpublish' 
  | 'archive' 
  | 'restore';

export type EntityType = 
  | 'product' 
  | 'attribute' 
  | 'category' 
  | 'media' 
  | 'translation' 
  | 'variant';

// Form Data Types
export interface WorkflowTransitionData {
  toState: WorkflowState;
  comment?: string;
  assignedReviewerId?: number;
  dueDate?: string;
  priority?: WorkflowPriority;
  rejectionReason?: string;
}

export interface CommentFormData {
  comment: string;
  type?: CommentType;
  isInternal?: boolean;
  parentId?: number;
  attachments?: any[];
}

export interface RevisionFormData {
  title?: string;
  description?: string;
  changesSummary?: string;
  tags?: string[];
  includeAttributes?: boolean;
  includeCategories?: boolean;
  includeMedia?: boolean;
  includeTranslations?: boolean;
}

// Filter Types
export interface WorkflowFilters {
  state?: WorkflowState;
  assignedTo?: number;
  priority?: WorkflowPriority;
  overdue?: boolean;
  page?: number;
  limit?: number;
}

export interface CommentFilters {
  includeInternal?: boolean;
  includeResolved?: boolean;
  type?: CommentType;
  page?: number;
  limit?: number;
}

export interface RevisionFilters {
  includeCurrent?: boolean;
  page?: number;
  limit?: number;
}

// Response Types
export interface WorkflowStatistics {
  stateDistribution: Array<{ current_state: WorkflowState; count: number }>;
  priorityDistribution: Array<{ priority: WorkflowPriority; count: number }>;
  overdueCount: number;
  workflowActivity: Array<{ action_type: WorkflowActionType; to_state: WorkflowState; count: number }>;
  avgProcessingTimes: Record<WorkflowState, number>;
}

export interface CommentStatistics {
  total: number;
  byType: Record<CommentType, { resolved: number; unresolved: number; total: number }>;
  resolved: number;
  unresolved: number;
}

export interface RevisionStatistics {
  totalRevisions: number;
  revisionsByUser: Array<{ created_by: number; count: number; creator: User }>;
  averageSize: number;
  revisionFrequency: Array<{ month: string; count: number }>;
}

export interface BulkWorkflowOperation {
  operation: 'assign_reviewer' | 'set_priority' | 'set_due_date';
  productIds: number[];
  data: {
    reviewerId?: number;
    priority?: WorkflowPriority;
    dueDate?: string;
  };
}

export interface BulkCommentOperation {
  operation: 'resolve' | 'unresolve' | 'delete' | 'change_type';
  commentIds: number[];
  data?: {
    type?: CommentType;
  };
}

export interface BulkRevisionOperation {
  operation: 'delete' | 'update_tags';
  revisionIds: number[];
  data?: {
    tags?: string[];
  };
}

// Workflow State Definitions
export const WORKFLOW_STATES = [
  { value: 'draft', label: 'Draft', color: 'gray', description: 'Product is being created/edited' },
  { value: 'pending_review', label: 'Pending Review', color: 'yellow', description: 'Waiting for reviewer assignment' },
  { value: 'in_review', label: 'In Review', color: 'blue', description: 'Currently being reviewed' },
  { value: 'approved', label: 'Approved', color: 'green', description: 'Ready for publishing' },
  { value: 'published', label: 'Published', color: 'emerald', description: 'Live and visible to customers' },
  { value: 'rejected', label: 'Rejected', color: 'red', description: 'Needs changes before proceeding' },
  { value: 'archived', label: 'Archived', color: 'slate', description: 'No longer active' }
] as const;

export const WORKFLOW_PRIORITIES = [
  { value: 'low', label: 'Low', color: 'gray' },
  { value: 'medium', label: 'Medium', color: 'blue' },
  { value: 'high', label: 'High', color: 'orange' },
  { value: 'urgent', label: 'Urgent', color: 'red' }
] as const;

export const COMMENT_TYPES = [
  { value: 'general', label: 'General Comment', color: 'blue' },
  { value: 'review', label: 'Review Comment', color: 'yellow' },
  { value: 'approval', label: 'Approval Comment', color: 'green' },
  { value: 'rejection', label: 'Rejection Comment', color: 'red' },
  { value: 'internal', label: 'Internal Note', color: 'gray' }
] as const;





// User interface for workflow
export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  initials?: string;
  role?: string;
}

